// @flow
/* eslint-disable no-underscore-dangle */
import supportedLangModules from './supportedLangModules.macro'
import importWithRetry from 'mz-utils/importWithRetry'

const pluralLocaleModules = supportedLangModules({
  modulePrefix: '@formatjs/intl-pluralrules/locale-data',
  localeField: 'intl',
  chunkPrefix: 'plural-locale',
})
const displayNamesLocaleModules = supportedLangModules({
  modulePrefix: '@formatjs/intl-displaynames/locale-data',
  localeField: 'intl',
  chunkPrefix: 'displaynames-locale',
})
const relativeTimeLocaleModules = supportedLangModules({
  modulePrefix: '@formatjs/intl-relativetimeformat/locale-data',
  localeField: 'intl',
  chunkPrefix: 'relativetime-locale',
})
const listFormatLocaleModules = supportedLangModules({
  modulePrefix: '@formatjs/intl-listformat/locale-data',
  localeField: 'intl',
  chunkPrefix: 'listformat-locale',
})
const numberFormatLocaleModules = supportedLangModules({
  modulePrefix: '@formatjs/intl-numberformat/locale-data',
  localeField: 'intl',
  chunkPrefix: 'nubmerformat-locale',
})
const dateTimeFormatLocaleModules = supportedLangModules({
  modulePrefix: '@formatjs/intl-datetimeformat/locale-data',
  localeField: 'intl',
  chunkPrefix: 'datetimeformat-locale',
})

function injectRelativeTimePolyfill(localeName: string) {
  return importWithRetry(() =>
    import(
      /* webpackChunkName: "intl-relativetimeformat" */
      '@formatjs/intl-relativetimeformat/polyfill'
    )
  ).then(() => importWithRetry(relativeTimeLocaleModules[localeName]))
}

function injectPluralPolyfill(localeName: string) {
  return importWithRetry(() =>
    import(
      /* webpackChunkName: "intl-plural" */
      '@formatjs/intl-pluralrules/polyfill'
    )
  ).then(() => importWithRetry(pluralLocaleModules[localeName]))
}

function injectDisplayNamePolyfill(localeName: string) {
  return importWithRetry(() =>
    import(
      /* webpackChunkName: "intl-displaynames" */
      '@formatjs/intl-displaynames/polyfill'
    )
  ).then(() => importWithRetry(displayNamesLocaleModules[localeName]))
}

function injectListFormatPolyfill(localeName: string) {
  return importWithRetry(() =>
    import(
      /* webpackChunkName: "intl-listformat" */
      '@formatjs/intl-listformat/polyfill'
    )
  ).then(() => importWithRetry(listFormatLocaleModules[localeName]))
}

function injectLocalePolyfill() {
  return importWithRetry(() =>
    import(
      /* webpackChunkName: "intl-locale" */
      '@formatjs/intl-locale/polyfill'
    )
  )
}

function injectNumberFormat(localeName: string) {
  return importWithRetry(() =>
    import(
      /* webpackChunkName: "intl-nubmerformat" */
      '@formatjs/intl-numberformat/polyfill'
    )
  ).then(() => importWithRetry(numberFormatLocaleModules[localeName]))
}

function injectDateTimeFormat(localeName: string) {
  return importWithRetry(() =>
    import(
      /* webpackChunkName: "intl-datetimeformat" */
      '@formatjs/intl-datetimeformat/polyfill'
    )
  ).then(() => importWithRetry(dateTimeFormatLocaleModules[localeName]))
}

function shouldInjectPolyfill(type: string, localeName: string) {
  if (
    typeof Intl[type] === 'undefined' ||
    // $FlowFixMe: Flow doesn't know about that field for some reason
    typeof Intl[type].supportedLocalesOf === 'undefined'
  ) {
    return true
  }

  const test = [localeName]
  const supported = Intl[type].supportedLocalesOf(test)

  if (supported.length < test.length) {
    return true
  }

  return false
}

function loadRelativeTimeLocale(localeName: string): Promise<*> {
  if (shouldInjectPolyfill('RelativeTimeFormat', localeName)) {
    return injectRelativeTimePolyfill(localeName)
  }
  return Promise.resolve()
}

function loadPluralRules(localeName: string): Promise<*> {
  if (shouldInjectPolyfill('PluralRules', localeName)) {
    return injectPluralPolyfill(localeName)
  }
  return Promise.resolve()
}

function loadNumberFormat(localeName: string): Promise<void> {
  if (shouldInjectPolyfill('NumberFormat', localeName)) {
    return injectNumberFormat(localeName)
  }
  return Promise.resolve()
}

function loadDisplayNames(localeName: string): Promise<void> {
  // $FlowFixMe: Flow doesn't know about that field for some reason
  if (shouldInjectPolyfill('DisplayNames', localeName)) {
    return injectDisplayNamePolyfill(localeName)
  }
  return Promise.resolve()
}

function loadListFormat(localeName: string): Promise<void> {
  // $FlowFixMe: Flow doesn't know about that field for some reason
  if (shouldInjectPolyfill('ListFormat', localeName)) {
    return injectListFormatPolyfill(localeName)
  }
  return Promise.resolve()
}

function loadDateTimeFormat(localeName: string): Promise<void> {
  if (shouldInjectPolyfill('DateTimeFormat', localeName)) {
    return injectDateTimeFormat(localeName)
  }
  return Promise.resolve()
}

function loadLocale(): Promise<void> {
  // $FlowFixMe: Flow doesn't know about that field for some reason
  if (typeof Intl.Locale === 'undefined') {
    return injectLocalePolyfill()
  }
  return Promise.resolve()
}

function loadCanonicalPolyfill(): Promise<void> {
  if (typeof Intl.getCanonicalLocales === 'undefined') {
    return importWithRetry(() =>
      import(
        /* webpackChunkName: "intl-getcanonicallocales" */
        '@formatjs/intl-getcanonicallocales/polyfill'
      )
    )
  }
  return Promise.resolve()
}

/**
 * Root function that loads all necessary Intl polyfills
 * @param  {string} localeName
 * @return {Promise}
 */
function loadPolyfills(localeName: string): Promise<*> {
  if (!global.Intl) {
    global.Intl = {}
  }

  return loadCanonicalPolyfill()
    .then(() =>
      Promise.all([
        loadPluralRules(localeName),
        loadDisplayNames(localeName),
        loadLocale(),
        loadListFormat(localeName),
      ])
    )
    .then(() =>
      Promise.all([
        loadRelativeTimeLocale(localeName),
        loadNumberFormat(localeName),
      ])
    )
    .then(() => loadDateTimeFormat(localeName))
}

export default loadPolyfills
